import React, { FC, useMemo, useContext, useState, useRef, useEffect, Fragment } from "react";

import InfoIcon from '@pixel/assets/icons/Info'
import HelpIcon from '@pixel/assets/icons/Help'

import AutoSelect from '@pixel/core/AutoSelect';
import CircularProgress from '@pixel/core/CircularProgress';
import ContentModal from "@pixel/core/ContentModal";
import FileUpload from '@pixel/core/FileUpload/FileUpload';
import FormControl from '@pixel/core/FormControl';
import FormInputLayout from '@pixel/core/FormInputLayout';
import FormLabel from '@pixel/core/FormLabel';
import FormControlLabel from '@pixel/core/FormControlLabel';
import TextField from '@pixel/core/TextField';
import Typography from "@pixel/core/Typography";
import ToolTip from '@pixel/core/Tooltip';
import Radio from '@pixel/core/Radio';
import RadioGroup from '@pixel/core/RadioGroup';
import Stack from '@pixel/core/Stack';
import { useSnackbar } from '@pixel/core/SnackbarService'

import HTMLEditor from '@pixel/internal/Editor/HTMLEditor';

import CandidateListContext from "../../contexts/candidate/candidateListContext";
import {
  useGetCandidateMessageTemplates,
  useSendCandidateMessageReq,
  CandidateMessageTemplateWithLocale,
} from '../../hooks/useSendCandidateMessage'
import { UserEmailTemplate } from "../../hooks/useUserEmailTemplates";
import UserEmailTemplatesInput from "../UserEmailTemplatesInput";
import { useLocaleType, useMultipleLocalizedText } from "../../contexts/Localization/localizationHooks";
import { useJHireURL } from "../../contexts/Domain";
import formatPluralString from '../../utils/formatPluralString'
import convertLineBreaksToBR, { removeLineBreaks } from "../../utils/formatEmailBody";
import FloatingCircularProgress from "./FloatingCircularProgress";

type FromTypes = 'myself' | 'anonymously'
type TemplateTypes = 'company' | 'personal'
interface ICallback {
  (): void;
}

interface ActionModalProps {
  open: boolean;
  onClose: ICallback;
}

const accept = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/pdf': ['.pdf'],
  'application/dmg': ['.dmg'],
  'application/doc': ['.doc'],
}

const SendMessageModal: FC<ActionModalProps> = ({
  open = false,
  onClose = () => { },
}) => {
  const jhireURL = useJHireURL()
  const userLocalePreference = useLocaleType()
  const {
    SEND_ANONYMOUSLY,
    COMMON_CANDIDATE,
    COMMON_CANDIDATES,
    COMMON_SUBJECT,
    COMMON_FROM,
    COMMON_TEMPLATE,
    COMMON_TEMPLATES,
    CANCEL,
    COMMON_MESSAGE_SEND,
    COMMON_SUBJECT_IS_REQUIRED,
    PERSONAL_EMAIL_TEMPLATES_COMPANY_TEMPLATES,
    PERSONAL_EMAIL_TEMPLATES_MY_TEMPLATES,
    JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD,
    JV_SENDMESSAGE_CONTROLLER_CHOOSE_THE_SEND_ANONYMOUSLY_OPTION_IF_YOU_DO_NOT_W,
    COMMON_FILE_UPLOAD,
    COMMON_FILE_DROP,
    JV_SENDMESSAGE_CONTROLLER_MESSAGE_WAS_SENT_SUCCESSFULLY_TO_1_CANDIDATE,
    JV_SENDMESSAGE_CONTROLLER_MESSAGE_WAS_SENT_SUCCESSFULLY_TO_0_CANDIDATES,
    JV_SENDMESSAGE_CONTROLLER_MEESAGE_SENT_ERROR,
  } = useMultipleLocalizedText()

  const snack = useSnackbar()
  const {
    validatedCandidates,
    validatedCandidateIds,
  } = useValidatedCandidatesList()

  const sendCandidateMessageProps = useMemo(() => ({
    applications: validatedCandidateIds,
  }), [validatedCandidateIds])

  const {
    loading: templatesLoading,
    error: templatesError,
    templates,
  } = useGetCandidateMessageTemplates(sendCandidateMessageProps)

  const {
    loading: sendLoading,
    error: sendError,
    response: sendResponse,
    sendMessageRequest,
  } = useSendCandidateMessageReq()

  const [from, setFrom] = useState<FromTypes>('myself')
  const [templateType, setTemplateType] = useState<TemplateTypes>('company')
  const [templateChosen, setTemplateChosen] = useState<CandidateMessageTemplateWithLocale | UserEmailTemplate>(null)
  const templateRef = useRef(null)
  templateRef.current = templateChosen

  const [subject, setSubject] = useState('')
  const [subjectError, setSubjectError] = useState('')

  const [files, setFiles] = useState([])

  const bodyEditorRef = useRef(null)
  const [contentError, setContentError] = useState('')

  useEffect(() => {
    setTemplateChosen(null);
    if (templateType === 'company') {
      const userLocaleTemplates = templates?.filter(template => template.locale.toLowerCase() === userLocalePreference.toLowerCase()) || []
      const defaultTemplate = userLocaleTemplates.find(template => template.isDefault)
      if (defaultTemplate) {
        setTemplateChosen(defaultTemplate)
      } else if (templates.length > 0) {
        setTemplateChosen(templates[0])
      }
    }
  }, [templates, templateType])

  useEffect(() => {
    if (templateChosen) {
      setSubject(templateChosen.subject)
      if (bodyEditorRef.current) {
        bodyEditorRef.current.setValue(convertLineBreaksToBR(templateChosen.body))
      }
    } else {
      setSubject('');
      if (bodyEditorRef?.current) {
        bodyEditorRef.current.setValue('');
      }
    }
  }, [templateChosen])

  const handleReject = () => {

  };
  const handleResolve = () => {
    setSubjectError('')
    setContentError('')

    let hasError = false

    if (subject === '') {
      setSubjectError(COMMON_SUBJECT_IS_REQUIRED)
      hasError = true
    }

    if (bodyEditorRef.current.getValue() === '') {
      setContentError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
      hasError = true
    }

    if (hasError) {
      return true
    }

    sendMessageRequest({
      sendAnonymously: from === 'anonymously',
      entityIdList: validatedCandidateIds,
      attachmentList: files.map(file => file.response.resourceInfo.eid),
      locale: (templateChosen && templateChosen.locale) ? templateChosen.locale : undefined,
      subject: subject,
      content: removeLineBreaks(bodyEditorRef.current.getValue()),
    })
      .then((response) => {
        if (validatedCandidates.length === 1) {
          snack.showSuccess(JV_SENDMESSAGE_CONTROLLER_MESSAGE_WAS_SENT_SUCCESSFULLY_TO_1_CANDIDATE)
        } else {
          const message = JV_SENDMESSAGE_CONTROLLER_MESSAGE_WAS_SENT_SUCCESSFULLY_TO_0_CANDIDATES
            .replace('{0}', validatedCandidates.length)

          snack.showSuccess(message)
        }
        onClose()
      })
      .catch((err) => {
        snack.showError(JV_SENDMESSAGE_CONTROLLER_MEESAGE_SENT_ERROR)
      })

    return true
  };

  const handleHTMLEditorLoad = () => {
    if (bodyEditorRef.current && templateRef.current) {
      bodyEditorRef.current.setValue(convertLineBreaksToBR(templateRef.current.body))
    }
  }

  const validNamesList = useMemo(() => {
    if (validatedCandidates.length > 0) {
      return validatedCandidates.map(candidate => <Fragment key={candidate.EId}>{candidate.candidate.fullName} <br /></Fragment>)
    }

    return ''
  }, [validatedCandidates])

  return (
    <ContentModal
      id="send-message-candidate"
      open={open}
      onClose={onClose}
      showClose={true}
      sizeVariant="md"
      title={COMMON_MESSAGE_SEND}
      labelCloseAria="close"
      rejectActions={[{ reason: "cancel", label: CANCEL }]}
      resolveActions={[{ reason: "ok", label: COMMON_MESSAGE_SEND }]}
      onResolve={handleResolve}
      onReject={handleReject}
      modalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
      }}
    >
      <Stack>
        {templatesLoading && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        )}
        {
          !templatesLoading && (
            <>
              <FloatingCircularProgress open={sendLoading} />
              <ToolTip title={<>{validNamesList}</>} placement="bottom-start">
                <div>
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    {validNamesList.length} {' '}
                    {formatPluralString(
                      validNamesList.length,
                      COMMON_CANDIDATE,
                      COMMON_CANDIDATES,
                    )}
                    <InfoIcon sx={{ marginLeft: '5px' }} />
                  </Typography>
                </div>
              </ToolTip>
              <FormControl>
                <FormLabel id="from-label">{COMMON_FROM}</FormLabel>
                <RadioGroup
                  row
                  value={from}
                  onChange={(e, value) => setFrom(value)}
                  aria-labelledby="from-label"
                >
                  <FormControlLabel value="myself" label={window._jvselfUser.fullName} control={<Radio />} />
                  <FormControlLabel value="anonymously" label={
                    <div style={{ display: 'flex' }}>
                      {SEND_ANONYMOUSLY}
                      <ToolTip title={JV_SENDMESSAGE_CONTROLLER_CHOOSE_THE_SEND_ANONYMOUSLY_OPTION_IF_YOU_DO_NOT_W} placement="bottom-start">
                        <div>
                          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                            <HelpIcon sx={{ marginLeft: '5px' }} />
                          </Typography>
                        </div>
                      </ToolTip>
                    </div>
                } control={<Radio />} />
                </RadioGroup>
              </FormControl>
              {window.properties.personalEmailTemplatesEnabled && (
                <FormControl>
                  <FormLabel id="templates-label">{COMMON_TEMPLATES}</FormLabel>
                  <RadioGroup
                    row
                    value={templateType}
                    onChange={(e, value) => setTemplateType(value)}
                    aria-labelledby="templates-label"
                  >
                    <FormControlLabel value="company" label={PERSONAL_EMAIL_TEMPLATES_COMPANY_TEMPLATES} control={<Radio />} />
                    <FormControlLabel value="personal" label={PERSONAL_EMAIL_TEMPLATES_MY_TEMPLATES} control={<Radio />} />
                  </RadioGroup>
                </FormControl>
              )}
              <FormInputLayout gridLayout="calc(100%)">
                {templateType === 'company' && (
                  <AutoSelect
                    label={COMMON_TEMPLATE}
                    value={templateChosen}
                    options={templates}
                    groupBy={(option) => option.group}
                    onChange={(e, value) => setTemplateChosen(value)}
                    getOptionLabel={option => option.title}
                    isOptionEqualToValue={(option, value) => option.EId === value.EId}
                  />
                )}
                {templateType === 'personal' && (
                  <UserEmailTemplatesInput
                    // @ts-ignore
                    value={templateChosen}
                    onChange={(event, newTemplate: any) => {
                      setTemplateChosen(newTemplate)
                    }}
                  />
                )}
              </FormInputLayout>
              <FormInputLayout gridLayout="calc(100%)">
                <TextField
                  required
                  label={COMMON_SUBJECT}
                  fullWidth
                  margin="normal"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  errorText={subjectError}
                  error={subjectError !== ''}
                />
              </FormInputLayout>
              <HTMLEditor
                ref={bodyEditorRef}
                variant="standard"
                mergeFields={{}}
                disabled={false}
                initialValue={convertLineBreaksToBR(((templateChosen || {}).body) || '')}
                onLoad={handleHTMLEditorLoad}
              />
              { contentError && <Typography sx={{ color: 'red' }}>{JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD}</Typography> }
              <div style={{ marginTop: '24px' }} />
              <FileUpload
                btnMultiLabel={COMMON_FILE_UPLOAD}
                dragMultiLabel={COMMON_FILE_DROP}
                maxFiles={4}
                accept={accept}
                multiple
                uploadUrl={`${jhireURL}/action/resource/attachment?&getDownloadToken=true`}
                onChange={(files) => setFiles(files)}
              />
            </>
          )}
      </Stack>
    </ContentModal>
  );
};

export default SendMessageModal;

function useValidatedCandidatesList() {
  const context = useContext(CandidateListContext);

  if (context === null) {
    throw 'This hook must be used within a <CandidateActionModal /> child'
  }

  return useMemo(() => {
    if (context.validatedCandidateList && context.validatedCandidateList.validApplications) {
      return {
        validatedCandidates: context.validatedCandidateList.validApplications,
        validatedCandidateIds: context.validatedCandidateList.validApplicationIds,
      }
    }

    return {
      validatedCandidates: [],
      validatedCandidateIds: [],
    }
  }, [
    context,
    context.validatedCandidateList,
    context.validatedCandidateList.validApplication,
  ])
}
